import CommentModel from '@/models/comments/commentModel'

const getDefaultState = () => ({
  cached: {
    candidate: {
      comments: [],
      filters: {
        searchTerm: '',
        commentTypes: [],
        fromDate: '',
        untilDate: '',
      },
    },
    pagination: {
      pageSize: 10,
      pageNumber: 1,
      totalPages: 1,
      nextPageLink: '',
      previousPageLink: '',
      totalCount: 0,
    },
    allCommentTypes: [],
    module: '',
    isLoading: 0,
  },
})

const state = getDefaultState()

const mapComments = (comments) =>
  comments.map(
    (comment) =>
      new CommentModel({ ...comment, addedBy: comment.addedBy ?? '' })
  )

export default {
  namespaced: true,
  state,

  getters: {
    getComments: (state) => (entityId) => {
      const { module, pagination } = state.cached
      if (module?.length) {
        const entityComments = state.cached[module].comments.find(
          ({ id }) => id === entityId
        )
        if (entityComments?.commentPages?.length) {
          const pageComments = entityComments.commentPages.find(
            ({ page }) => page === pagination.pageNumber
          )
          return pageComments?.comments || []
        }
      }
      return []
    },
  },
  mutations: {
    ADD_COMMENTS(state, { entityId, comments }) {
      const { module } = state.cached
      const entityComments = state.cached[module].comments
      const existingIndex = entityComments.findIndex(
        ({ id }) => id === entityId
      )

      const commentPage = [{ page: 1, comments }]

      if (existingIndex > -1) {
        entityComments[existingIndex].commentPages = commentPage
      } else {
        entityComments.push({ id: entityId, commentPages: commentPage })
      }
    },
    ADD_COMMENT_PAGE(state, { entityId, comments, pageNumber }) {
      const { module } = state.cached
      const entityComments = state.cached[module].comments
      const entityIndex = entityComments.findIndex(({ id }) => id === entityId)

      entityComments[entityIndex].commentPages.push({
        page: pageNumber,
        comments,
      })
    },
    CLEAR_COMMENT_PAGES(state, entityId) {
      const { module } = state.cached
      const entityComments = state.cached[module].comments
      const entityIndex = entityComments.findIndex(({ id }) => id === entityId)
      if (entityIndex > -1) entityComments[entityIndex].commentPages = []
    },
    RESET_PAGINATION(state) {
      const { pageSize } = state.cached.pagination
      state.cached.pagination = {
        ...getDefaultState().cached.pagination,
        pageSize,
      }
    },
    SET_FILTERS(state, { key, value }) {
      const { module } = state.cached
      state.cached[module].filters[key] = value
    },
    SET_PAGINATION(state, { key, value }) {
      state.cached.pagination[key] = value
    },
    SET_CACHED_STATE(state, { key, value }) {
      state.cached[key] = value
    },
    SET_STATE(state, { key, value }) {
      state[key] = value
    },
    INCREMENT_LOADING(state) {
      state.cached.isLoading++
    },
    DECREMENT_LOADING(state) {
      state.cached.isLoading--
    },
  },

  actions: {
    async commentsInit({ state, commit, dispatch }, entityId) {
      commit('INCREMENT_LOADING')
      const { module, allCommentTypes } = state.cached
      if (!allCommentTypes?.length) {
        await dispatch('getCommentTypes')
      }
      const entityComments = state.cached[module].comments.find(
        ({ id }) => id === entityId
      )
      if (entityId && !entityComments?.commentPages?.length) {
        commit('SET_PAGINATION', { key: 'pageNumber', value: 1 })
        await dispatch('getComments', entityId)
      }
      commit('DECREMENT_LOADING')
    },

    async getComments({ state, commit, dispatch }, entityId) {
      const { module } = state.cached
      if (!module.length) return

      const { filters } = state.cached[module]

      const { pageSize, pageNumber } = state.cached.pagination

      let response
      if (module === 'candidate') {
        response = await this.$api.comments.getCommentsByCandidate(entityId, {
          pageSize,
          pageNumber,
          ...filters,
        })
      }

      if (response?.data) {
        const pagination = JSON.parse(response.headers['x-pagination'])
        dispatch('setPagination', pagination)
        const comments = mapComments(response.data)
        commit('ADD_COMMENTS', { entityId, comments })
      }
    },

    async addCommentsPage({ state, commit }, { entityId, navigationType }) {
      commit('INCREMENT_LOADING')
      const { module, pagination } = state.cached
      const { filters } = state.cached[module]
      let { pageSize, pageNumber } = pagination

      let pageContent

      if (navigationType === 'pageSelect') {
        if (module === 'candidate') {
          pageContent = await this.$api.comments.getCommentsByCandidate(
            entityId,
            { pageSize, pageNumber, ...filters }
          )
        }
      } else {
        const payload =
          navigationType === 'next'
            ? pagination.nextPageLink
            : pagination.prevPageLink
        pageContent = await this.$api.comments.paginationRequest(payload)

        pageNumber = JSON.parse(pageContent.headers['x-pagination']).currentPage
      }

      if (pageContent?.data) {
        const comments = mapComments(pageContent.data)
        commit('ADD_COMMENT_PAGE', { entityId, comments, pageNumber })
      }

      commit('DECREMENT_LOADING')
    },

    async getCommentTypes({ commit }) {
      const response = await this.$api.comments.getCommentTypes()
      commit('SET_CACHED_STATE', {
        key: 'allCommentTypes',
        value: response.data,
      })
    },

    setPagination({ commit }, pagination) {
      const {
        pageSize,
        currentPage,
        totalPages,
        totalCount,
        previousPageLink,
        nextPageLink,
      } = pagination
      commit('SET_PAGINATION', { key: 'pageSize', value: pageSize })
      commit('SET_PAGINATION', { key: 'pageNumber', value: currentPage })
      commit('SET_PAGINATION', { key: 'totalPages', value: totalPages })
      commit('SET_PAGINATION', { key: 'totalCount', value: totalCount })
      commit('SET_PAGINATION', { key: 'nextPageLink', value: nextPageLink })
      commit('SET_PAGINATION', {
        key: 'previousPageLink',
        value: previousPageLink,
      })
    },
  },
}
